import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { AlertRow } from "@/components/alerts/AlertRow";
import { NextThirtyDaysRow } from "@/components/dashboard/NextThirtyDaysRow";
import { StalledRenewalRow } from "@/components/dashboard/StalledRenewalRow";
import { StatTile } from "@/components/dashboard/StatTile";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import {
  BellIcon,
  BillsIcon,
  CheckIcon,
  ClockIcon,
  DocumentsIcon,
  EquipmentIcon,
  HomeIcon,
  MoneyIcon,
  PropertiesIcon,
  TenantsIcon,
} from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import { formatBytes, formatDateCell, formatOutcome, formatPercentCell } from "@/lib/format";
import type {
  AlertInstanceResource,
  DashboardSummaryResource,
  LastSuccessfulRunResource,
  NextThirtyDaysItemResource,
  Page,
} from "@/lib/api-types";

/**
 * Home is `/` (spec judgement call 2) — Story 13.1 replaces the Epic-1
 * placeholder with the dashboard's first two real panels: "Needs attention
 * now" (R-H1, alert-instance-driven) and "Next 30 days" (R-H2,
 * date-driven). Stacked vertically (spec judgement call 2: exact layout is
 * a UI call within this story's own scope) — DESIGN.md's own dashboard
 * vocabulary is "stat tiles plus alert rows plus lists," so both panels
 * reuse `AlertRow`/a plain list, never a new visual primitive.
 *
 * Story 13.2 adds three more panels to this same page: "Portfolio at a
 * glance" (R-H4 — properties, rentable units, occupancy %; "monthly rent
 * roll"/"YTD net" are deliberately not two further tiles here, R-H7 — see
 * `app/domain/dashboard/portfolio_summary.py`'s own module docstring),
 * "Renewal pipeline" (R-H3 — a real count for every `RenewalStatus` bucket,
 * zero included, plus the distinct stalled-conversation list), and "Recent
 * activity" (R-H6 — the real last 10 audit-log rows).
 *
 * Story 13.3 adds the sixth and final panel, "Data health" (R-H5) — "is the
 * app itself okay," separate from "what needs attention on a property":
 * the real last-successful-backup/restore-test dates (an honest "No backup
 * has run yet"/"No restore test has run yet" when neither table has ever
 * recorded a success, VC-13.3-03, never a blank field or a fabricated
 * date), the real portfolio-wide `Details needed` equipment count, the real
 * count of municipalities with incomplete setup, the real count of
 * properties with an unconfirmed (`IS NULL`, never `false`) escrow flag,
 * and the real total document storage used against the configured alert
 * threshold (`app/jobs/evaluator.py`'s own `storage-threshold` rule reads
 * that exact same threshold and the exact same `total_document_bytes()`
 * figure — never a second, competing byte count). Rendered as six
 * `StatTile`s (mirroring "Portfolio at a glance"'s own identical grid
 * shape) off the same `summaryQuery` the three Story 13.2 panels already
 * share — a seventh field on that one bundled read, not a new route.
 *
 * All six panels render unconditionally, each with its own real, honest
 * empty state when its own query legitimately returns nothing — R-H7's
 * "hidden, not empty" applies to *unbuilt* panels (absent from this page
 * entirely), never to a shipped one rendering blank.
 *
 * "Needs attention now" renders every `AlertRow` with `canMutate={false}` —
 * acknowledge/snooze stay the Alert Centre's own job (`/alerts`,
 * `AlertCentre.tsx`); wiring those mutations into the dashboard itself is
 * outside this story's scope (not named anywhere in the Acceptance
 * Criteria), so the two handlers below are never actually invoked.
 *
 * The three Story 13.2 panels share one query (`GET /dashboard/summary`,
 * that story's own judgement call 2 — "one dashboard load, one round trip"),
 * so they share one loading/error state too; "Needs attention now"/"Next 30
 * days" keep their own two separate Story 13.1 queries, unchanged.
 */

export const NEEDS_ATTENTION_QUERY_KEY = ["dashboard", "needs-attention"] as const;
export const NEXT_30_DAYS_QUERY_KEY = ["dashboard", "next-30-days"] as const;
export const DASHBOARD_SUMMARY_QUERY_KEY = ["dashboard", "summary"] as const;

/**
 * Owner-requested (2026-08-28): "just say when the last successful backup
 * was, and if it's older than 3 days turn red." Computed client-side off
 * the same `run_at` the tile already displays — the backend's own
 * `backup-missing` alert rule (`app/domain/alerting/rules.py`) is the real,
 * durable, Owner-editable source of truth for "should this actually alert
 * someone" (its own default threshold is 72 hours, the identical 3-day
 * window, but is a separate, editable setting — this card's own visual
 * threshold is intentionally the same number today, not read from that
 * rule, since a dashboard tile recomputing every render has no reason to
 * fetch a second resource just to duplicate one constant).
 */
const BACKUP_STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * `now` is an explicit parameter, never read internally via `Date.now()` —
 * mirrors `GlobalTopBar.tsx`'s own identical `greeting(now: Date)` shape:
 * the real call site below always supplies a real `new Date()`, but this
 * keeps the pure comparison itself directly testable against a fixed
 * instant rather than whatever day a test happens to run on.
 */
export function isBackupStale(lastSuccessfulBackup: LastSuccessfulRunResource | null, now: Date): boolean {
  if (!lastSuccessfulBackup) {
    return true;
  }
  return now.getTime() - new Date(lastSuccessfulBackup.run_at).getTime() > BACKUP_STALE_AFTER_MS;
}

function _noopAcknowledge(): void {
  // AlertRow's props are required even when canMutate={false} withholds the
  // buttons that would ever call them — see the module docstring.
}

function _noopSnooze(): void {}

/**
 * EXPLORATORY SPIKE (2026-08-27) — Owner-requested: give each list section
 * below "Portfolio at a glance" a table-style header bar (matching
 * `DataTable`'s own new tinted-header treatment) instead of a plain `<h2>`
 * floating above a bordered list, and a compact 2-3 word row instead of the
 * full `EmptyState` block when a section has nothing to show — a section
 * that legitimately has zero rows should read like an empty table, not a
 * large centred announcement. Local to this file: no other page uses this
 * shape yet.
 */
const SECTION_TINT_CLASSES = {
  indigo: "bg-vibrant-indigo-quiet",
  cyan: "bg-vibrant-cyan-quiet",
  violet: "bg-vibrant-violet-quiet",
  amber: "bg-vibrant-amber-quiet",
} as const;

function SectionCard({
  title,
  level = 2,
  tint,
  isEmpty,
  emptyLabel,
  children,
}: {
  title: string;
  /** The card's own header bar doubles as the section's real heading — no
   * separate, duplicate `<h2>` floats above it (live-caught: the title
   * was rendering twice). `level` picks `<h2>`/`<h3>` so "Renewal
   * pipeline"'s two cards (status counts, stalled conversations) still
   * nest correctly for assistive tech even though both look identical. */
  level?: 2 | 3;
  tint: keyof typeof SECTION_TINT_CLASSES;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
}) {
  const Heading = level === 2 ? "h2" : "h3";
  return (
    <div className="mt-4 overflow-hidden rounded-lg bg-surface shadow-card">
      <Heading
        className={`flex h-table-header items-center px-4 text-label font-medium text-ink-secondary ${SECTION_TINT_CLASSES[tint]}`}
      >
        {title}
      </Heading>
      {isEmpty ? <p className="px-4 py-6 text-center text-body text-ink-secondary">{emptyLabel}</p> : children}
    </div>
  );
}

export function Home() {
  useEffect(() => {
    document.title = "Home — PropDesk";
  }, []);

  const needsAttentionQuery = useQuery({
    queryKey: NEEDS_ATTENTION_QUERY_KEY,
    queryFn: () => apiFetch<Page<AlertInstanceResource>>("/dashboard/needs-attention"),
  });

  const next30DaysQuery = useQuery({
    queryKey: NEXT_30_DAYS_QUERY_KEY,
    queryFn: () => apiFetch<Page<NextThirtyDaysItemResource>>("/dashboard/next-30-days"),
  });

  const summaryQuery = useQuery({
    queryKey: DASHBOARD_SUMMARY_QUERY_KEY,
    queryFn: () => apiFetch<DashboardSummaryResource>("/dashboard/summary"),
  });

  const needsAttentionRows = needsAttentionQuery.data?.data ?? [];
  const needsAttentionIsEmpty = needsAttentionQuery.data !== undefined && needsAttentionRows.length === 0;
  const needsAttentionError =
    needsAttentionQuery.error instanceof ApiError ? needsAttentionQuery.error.message : undefined;
  // Owner-requested: a real "Open alerts" stat in "Portfolio at a glance",
  // reusing this same page's own already-fetched count rather than a
  // second, competing alert-total query (AlertBell's own /alert-instances
  // total answers a related but distinct question — every open alert
  // app-wide — while this page's own needs-attention total is the exact
  // number the section directly below the stat tile shows).
  const openAlertsCount = needsAttentionQuery.data?.meta.total;

  const next30DaysRows = next30DaysQuery.data?.data ?? [];
  const next30DaysIsEmpty = next30DaysQuery.data !== undefined && next30DaysRows.length === 0;
  const next30DaysError = next30DaysQuery.error instanceof ApiError ? next30DaysQuery.error.message : undefined;

  const summaryError = summaryQuery.error instanceof ApiError ? summaryQuery.error.message : undefined;
  const portfolio = summaryQuery.data?.portfolio;
  const renewalPipeline = summaryQuery.data?.renewal_pipeline;
  const dataHealth = summaryQuery.data?.data_health;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Home</h1>

      <section className="mt-8">
        <h2 className="text-heading font-semibold text-ink">Portfolio at a glance</h2>
        {summaryQuery.isPending || needsAttentionQuery.isPending ? (
          <p className="mt-4 text-body text-ink-secondary">Loading&hellip;</p>
        ) : summaryQuery.isError ? (
          <p role="alert" className="mt-4 text-body text-danger">
            {summaryError ?? "Something went wrong. Please try again."}
          </p>
        ) : portfolio ? (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <StatTile value={String(portfolio.property_count)} label="Properties" icon={PropertiesIcon} tint="indigo" />
            {/* Owner-requested (2026-08-27): count occupancy per property,
             * not per unit — a property with at least one tenant reads as
             * "rented," full stop, regardless of its own unit count. Backed
             * by `occupied_property_count`, a real, independently-computed
             * figure (not derived from the unit-level counts below). */}
            <StatTile
              value={String(portfolio.occupied_property_count)}
              label="Properties rented"
              icon={TenantsIcon}
              tint="violet"
            />
            <StatTile
              value={String(portfolio.property_count - portfolio.occupied_property_count)}
              label="Properties vacant"
              icon={HomeIcon}
              tint="cyan"
            />
            <StatTile
              value={formatPercentCell(portfolio.occupancy_percent)}
              label="Occupancy"
              icon={CheckIcon}
              tint="indigo"
            />
            {openAlertsCount !== undefined ? (
              <StatTile value={String(openAlertsCount)} label="Open alerts" icon={BellIcon} tint="amber" />
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="mt-10">
        {needsAttentionQuery.isPending ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Needs attention now</h2>
            <p className="mt-4 text-body text-ink-secondary">Loading&hellip;</p>
          </>
        ) : needsAttentionQuery.isError ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Needs attention now</h2>
            <p role="alert" className="mt-4 text-body text-danger">
              {needsAttentionError ?? "Something went wrong. Please try again."}
            </p>
          </>
        ) : (
          <SectionCard title="Needs attention now" tint="amber" isEmpty={needsAttentionIsEmpty} emptyLabel="No alerts">
            <ul
              aria-label={
                needsAttentionRows.length === 1
                  ? "1 item needing attention"
                  : `${needsAttentionRows.length} items needing attention`
              }
            >
              {needsAttentionRows.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  onAcknowledge={_noopAcknowledge}
                  onSnooze={_noopSnooze}
                  canMutate={false}
                />
              ))}
            </ul>
          </SectionCard>
        )}
      </section>

      <section className="mt-10">
        {next30DaysQuery.isPending ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Next 30 days</h2>
            <p className="mt-4 text-body text-ink-secondary">Loading&hellip;</p>
          </>
        ) : next30DaysQuery.isError ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Next 30 days</h2>
            <p role="alert" className="mt-4 text-body text-danger">
              {next30DaysError ?? "Something went wrong. Please try again."}
            </p>
          </>
        ) : (
          <SectionCard title="Next 30 days" tint="cyan" isEmpty={next30DaysIsEmpty} emptyLabel="Nothing due">
            <ul
              aria-label={
                next30DaysRows.length === 1 ? "1 item in the next 30 days" : `${next30DaysRows.length} items in the next 30 days`
              }
            >
              {next30DaysRows.map((item) => (
                <NextThirtyDaysRow key={item.id} item={item} />
              ))}
            </ul>
          </SectionCard>
        )}
      </section>

      <section className="mt-10">
        {summaryQuery.isPending ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Renewal pipeline</h2>
            <p className="mt-4 text-body text-ink-secondary">Loading&hellip;</p>
          </>
        ) : summaryQuery.isError ? (
          <>
            <h2 className="text-heading font-semibold text-ink">Renewal pipeline</h2>
            <p role="alert" className="mt-4 text-body text-danger">
              {summaryError ?? "Something went wrong. Please try again."}
            </p>
          </>
        ) : renewalPipeline ? (
          <>
            <SectionCard
              title="Renewal pipeline"
              tint="violet"
              isEmpty={renewalPipeline.counts_by_status.every((bucket) => bucket.count === 0)}
              emptyLabel="No active renewals"
            >
              <ul>
                {renewalPipeline.counts_by_status
                  .filter((bucket) => bucket.count > 0)
                  .map((bucket) => (
                    <li
                      key={bucket.status}
                      className="flex items-center justify-between gap-4 border-b border-border-hairline px-4 py-3 last:border-b-0"
                    >
                      <span className="text-body text-ink">{bucket.label}</span>
                      <span className="numeric-features text-body text-ink-secondary">{bucket.count}</span>
                    </li>
                  ))}
              </ul>
            </SectionCard>

            <SectionCard
              title={
                renewalPipeline.stalled_count === 1
                  ? "1 stalled conversation"
                  : `${renewalPipeline.stalled_count} stalled conversations`
              }
              level={3}
              tint="violet"
              isEmpty={renewalPipeline.stalled_count === 0}
              emptyLabel="None stalled"
            >
              <ul
                aria-label={
                  renewalPipeline.stalled_count === 1
                    ? "1 stalled renewal conversation"
                    : `${renewalPipeline.stalled_count} stalled renewal conversations`
                }
              >
                {renewalPipeline.stalled.map((item) => (
                  <StalledRenewalRow key={item.id} item={item} />
                ))}
              </ul>
            </SectionCard>
          </>
        ) : null}
      </section>

      <section className="mt-10">
        <h2 className="text-heading font-semibold text-ink">Data health</h2>
        {summaryQuery.isPending ? (
          <p className="mt-4 text-body text-ink-secondary">Loading&hellip;</p>
        ) : summaryQuery.isError ? (
          <p role="alert" className="mt-4 text-body text-danger">
            {summaryError ?? "Something went wrong. Please try again."}
          </p>
        ) : dataHealth ? (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile
              value={
                dataHealth.last_successful_backup
                  ? formatDateCell(dataHealth.last_successful_backup.run_at)
                  : "No backup has run yet"
              }
              label="Last successful backup"
              icon={ClockIcon}
              tint={isBackupStale(dataHealth.last_successful_backup, new Date()) ? "danger" : "indigo"}
            />
            <StatTile
              value={
                dataHealth.last_successful_restore_test
                  ? `${formatDateCell(dataHealth.last_successful_restore_test.run_at)} (${formatOutcome(dataHealth.last_successful_restore_test.outcome)})`
                  : "No restore test has run yet"
              }
              label="Last successful restore test"
              icon={ClockIcon}
              tint="cyan"
            />
            <StatTile
              value={String(dataHealth.equipment_details_needed_count)}
              label="Equipment: Details needed"
              icon={EquipmentIcon}
              tint="violet"
            />
            <StatTile
              value={String(dataHealth.incomplete_municipality_count)}
              label="Municipalities: incomplete setup"
              icon={BillsIcon}
              tint="amber"
            />
            <StatTile
              value={String(dataHealth.unconfirmed_escrow_count)}
              label="Unconfirmed escrow flags"
              icon={MoneyIcon}
              tint="indigo"
            />
            <StatTile
              value={`${formatBytes(dataHealth.storage_used_bytes)} of ${formatBytes(dataHealth.storage_alert_threshold_bytes)}`}
              label="Storage used"
              icon={DocumentsIcon}
              tint="cyan"
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}
